// src/pages/Statistics.js

import React, { useState, useEffect, useRef } from 'react';
import { db } from '../firebase';
import { collection, getDocs, onSnapshot, query, where } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, LineChart, Line, XAxis, YAxis, CartesianGrid } from 'recharts';
import OrderHistoryModal from '../components/modals/OrderHistoryModal'; // Asegurarse de que esta ruta sea correcta
import { FaInfoCircle, FaExclamationTriangle, FaBell, FaCheckCircle, FaCalendarTimes, FaClock, FaPercent } from 'react-icons/fa';

function Statistics() {
  const { currentUser, userProfile, loading: authLoading } = useAuth();
  const [totalOrders, setTotalOrders] = useState(0);
  const [ordersByStatus, setOrdersByStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [allOrders, setAllOrders] = useState([]);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const [monthlyOverdueData, setMonthlyOverdueData] = useState([]);
  const [monthlyOnTimeData, setMonthlyOnTimeData] = useState([]);
  const [monthlyAvgDeliveryTimeData, setMonthlyAvgDeliveryTimeData] = useState([]);

  const [overallOnTimeRate, setOverallOnTimeRate] = useState(0);
  const [overallOverdueCount, setOverallOverdueCount] = useState(0);
  const [overallAvgDeliveryTime, setOverallAvgDeliveryTime] = useState(0);
  const [overallRescheduledCount, setOverallRescheduledCount] = useState(0);

  // New state for chart time filter
  const [timeFilter, setTimeFilter] = useState('monthly'); // 'weekly', 'fortnightly', 'monthly'

  // Ref for time filter section (kept, but scroll useEffect is removed)
  const filterOptionsRef = useRef(null);


  const ordersCollectionRef = collection(db, 'pedidos');

  // Function to calculate the difference in days robustly using UTC
  const getDaysDifference = (dateString) => {
    if (!dateString || dateString === 'N/A') {
      return null; // Retorna null si la fecha no es válida o no existe
    }
    const today = new Date();
    // Get the start of today's day in UTC
    const todayUtc = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());

    // Parse the target date string as UTC directly to avoid timezone issues
    const parts = dateString.split('-').map(Number);
    // Añadir validación para la longitud del array de partes para evitar errores si dateString está mal formado
    if (parts.length !== 3 || isNaN(parts[0]) || isNaN(parts[1]) || isNaN(parts[2])) {
      console.warn("Invalid date string for getDaysDifference:", dateString);
      return null;
    }
    const targetDateUtc = Date.UTC(parts[0], parts[1] - 1, parts[2]); // Month is 0-indexed in Date.UTC

    const diffTime = targetDateUtc - todayUtc;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); // Round up to include the current day
    return diffDays;
  };

  // --- Lógica de "Vencido" corregida y mantenida ---
  // Un pedido se considera "Vencido" si:
  // 1. Su fecha prevista de entrega ya pasó Y su estado actual NO es un estado final.
  // 2. O si en ALGÚN momento de su historial, su fecha prevista ya había pasado Y su estado en ese momento NO era un estado final.
  const getDeliveryAlert = (order) => {
    // Asegurarse de que expectedDeliveryDate sea una cadena válida antes de crear la fecha
    if (!order.expectedDeliveryDate || order.expectedDeliveryDate === 'N/A') {
      return { text: 'Sin Fecha', color: '#999', icon: <FaInfoCircle /> };
    }
    
    const expectedDate = new Date(order.expectedDeliveryDate);
    const expectedDateUtc = Date.UTC(expectedDate.getFullYear(), expectedDate.getMonth(), expectedDate.getDate());
    const today = new Date();
    const todayUtc = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());

    // Define final statuses that mean the order is no longer "active" for overdue alerts
    const finalStatuses = ['Entregado Exitosamente', 'Pedido listo para Recojo', 'Entregado en Agencia'];

    // Check if it was ever overdue in its history
    let wasEverOverdue = false;
    for (const historyEntry of order.statusHistory || []) {
        const historyTimestamp = historyEntry.timestamp?.toDate ? historyEntry.timestamp.toDate() : new Date(historyEntry.timestamp);
        const historyTimestampUtc = Date.UTC(historyTimestamp.getFullYear(), historyTimestamp.getMonth(), historyTimestamp.getDate());

        // An order was overdue if its expected date passed BEFORE or AT the time of this status entry,
        // AND at that history point, its status was NOT a final status.
        // We compare expectedDateUtc with the timestamp of the history entry.
        if (expectedDateUtc < historyTimestampUtc && !finalStatuses.includes(historyEntry.status)) {
            wasEverOverdue = true;
            break;
        }
    }

    // If it was ever overdue based on its history, it remains "Vencido"
    if (wasEverOverdue) {
        return { text: 'Vencido', color: '#DC3545', icon: <FaExclamationTriangle /> }; // Red for historically overdue
    }

    // If it's currently in a final status, and wasn't historically overdue, it's Finalizado
    if (finalStatuses.includes(order.currentStatus)) {
      return { text: 'Finalizado', color: '#20C997', icon: <FaCheckCircle /> }; // Green for finished
    }

    // Otherwise, check current overdue status (if it wasn't historically overdue and not in a final status)
    const daysDiff = getDaysDifference(order.expectedDeliveryDate);

    if (daysDiff < 0) {
      return { text: 'Vencido', color: '#DC3545', icon: <FaExclamationTriangle /> }; // Red for currently overdue
    } else if (daysDiff === 0) {
      return { text: 'Por Vencer (hoy)', color: '#FD7E14', icon: <FaBell /> }; // Orange for today
    } else { // daysDiff > 0 (1 or more days in the future)
      return { text: `Por Vencer (${daysDiff} día(s))`, color: '#FFC107', icon: <FaInfoCircle /> }; // Yellow for upcoming
    }
  };
  // --- FIN Lógica de "Vencido" ---

  // Helper to get ISO week number (for weekly filter)
  const getWeekNumber = (d) => {
    d = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
    return `${d.getUTCFullYear()}-S${String(weekNo).padStart(2, '0')}`;
  };

  // Helper to generate the period key based on the filter
  const generatePeriodKey = (date, filter) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = date.getDate();

    switch (filter) {
      case 'weekly':
        return getWeekNumber(date);
      case 'fortnightly':
        return `${year}-${month}-Q${day <= 15 ? '1' : '2'}`;
      case 'monthly':
      default:
        return `${year}-${month}`;
    }
  };

  // Function to aggregate historical data and calculate overall indicators
  const aggregateDataAndCalculateOverall = (orders, filter) => {
    const now = new Date();
    const nowUtc = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()); // Normalize 'now' to UTC start of day

    // --- Determine dynamic start and end month for the chart range ---
    let minDate = new Date(now.getFullYear(), now.getMonth(), 1); // Default to start of current month
    let maxDate = now; // Default to current date

    if (orders.length > 0) {
        orders.forEach(order => {
            // Ensure createdAt and statusHistory timestamps are Date objects
            const orderCreatedAt = order.createdAt?.toDate ? order.createdAt.toDate() : (order.createdAt ? new Date(order.createdAt) : new Date());
            const expectedDelivery = new Date(order.expectedDeliveryDate);
            
            if (orderCreatedAt < minDate) minDate = orderCreatedAt;
            if (expectedDelivery < minDate) minDate = expectedDelivery;

            if (orderCreatedAt > maxDate) maxDate = orderCreatedAt;
            if (expectedDelivery > maxDate) maxDate = expectedDelivery;
        });
    }

    minDate = new Date(Date.UTC(minDate.getFullYear(), minDate.getMonth(), minDate.getDate()));
    maxDate = new Date(Date.UTC(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate(), 23, 59, 59, 999));


    // --- Initialize data structure per period ---
    const periodDataMap = new Map();
    let tempDate = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate()); // Start from the earliest date found

    while (tempDate <= maxDate) {
        const periodKey = generatePeriodKey(tempDate, filter);
        if (!periodDataMap.has(periodKey)) {
            periodDataMap.set(periodKey, {
                month: periodKey, // 'month' key is used by Recharts XAxis, but it's actually the period key
                overdueHistoricalIds: new Set(),
                onTimeCount: 0,
                totalCompletedCount: 0,
                totalDeliveryDays: 0,
                meta: 95 // Add meta for compliance chart
            });
        }

        // Advance tempDate based on filter
        switch (filter) {
            case 'weekly':
                tempDate.setDate(tempDate.getDate() + 7);
                break;
            case 'fortnightly':
                const currentDay = tempDate.getDate();
                if (currentDay <= 15) {
                    tempDate.setDate(16); // Move to start of second fortnight
                } else {
                    tempDate.setMonth(tempDate.getMonth() + 1);
                    tempDate.setDate(1); // Move to start of next month's first fortnight
                }
                break;
            case 'monthly':
            default:
                tempDate.setMonth(tempDate.getMonth() + 1);
                tempDate.setDate(1); // Move to start of next month
                break;
        }
    }

    // --- Calculation of Overall Indicators (Current Snapshot) ---
    let currentOverdueCount = 0;
    let currentOnTimeDeliveries = 0;
    let currentTotalCompletedDeliveries = 0;
    let currentTotalDeliveryDays = 0;
    let currentRescheduledCount = 0;

    orders.forEach(order => {
        const partsExpected = order.expectedDeliveryDate.split('-').map(Number);
        const expectedDateUtc = Date.UTC(partsExpected[0], partsExpected[1] - 1, partsExpected[2]);

        const deliveredStatusEntry = order.statusHistory?.find(h => h.status === 'Entregado Exitosamente');
        const actualDeliveryDateObj = deliveredStatusEntry?.timestamp ? (deliveredStatusEntry.timestamp.toDate ? deliveredStatusEntry.timestamp.toDate() : new Date(deliveredStatusEntry.timestamp)) : null;
        const actualDeliveryDateUtc = actualDeliveryDateObj ? Date.UTC(actualDeliveryDateObj.getFullYear(), actualDeliveryDateObj.getMonth(), actualDeliveryDateObj.getDate()) : null;

        // Lógica para el OverallOverdueCount (tarjeta) - Using getDeliveryAlert for current status
        // This now correctly reflects if it was EVER overdue.
        const alertStatus = getDeliveryAlert(order);
        if (alertStatus.text === 'Vencido') {
            currentOverdueCount++;
        }

        // For the general count of Rescheduled Orders (based on current status)
        if (order.currentStatus === 'Reprogramado') {
            currentRescheduledCount++;
        }

        // For Overall On-Time Rate and Average Delivery Time (based on final delivery)
        if (order.currentStatus === 'Entregado Exitosamente') {
            currentTotalCompletedDeliveries++;
            const orderDate = order.orderDate ? new Date(order.orderDate) : null;
            const orderDateUtc = orderDate ? Date.UTC(orderDate.getFullYear(), orderDate.getMonth(), orderDate.getDate()) : null;

            if (orderDateUtc && actualDeliveryDateUtc) {
                if (actualDeliveryDateUtc <= expectedDateUtc) {
                    currentOnTimeDeliveries++;
                }
                const diffTime = actualDeliveryDateUtc - orderDateUtc;
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                currentTotalDeliveryDays += diffDays;
            }
        }

        // --- Aggregation of Historical Data for Charts ---

        // 1. For On-Time Compliance Rate and Average Delivery Time (completed orders *in* a period)
        const deliveryEntry = order.statusHistory?.find(h => h.status === 'Entregado Exitosamente');
        if (deliveryEntry) {
            const actualDeliveryDateForChart = deliveryEntry.timestamp?.toDate ? deliveryEntry.timestamp.toDate() : new Date(deliveryEntry.timestamp);
            const periodKey = generatePeriodKey(actualDeliveryDateForChart, filter);
            
            const periodData = periodDataMap.get(periodKey);
            if (periodData) { // Only count if the period is within our historical range
                periodData.totalCompletedCount++;
                const partsExpectedForChart = order.expectedDeliveryDate.split('-').map(Number);
                const expectedDateForChartUtc = Date.UTC(partsExpectedForChart[0], partsExpectedForChart[1] - 1, partsExpectedForChart[2]);
                const actualDeliveryDateForChartUtc = Date.UTC(actualDeliveryDateForChart.getFullYear(), actualDeliveryDateForChart.getMonth(), actualDeliveryDateForChart.getDate());

                if (actualDeliveryDateForChartUtc <= expectedDateForChartUtc) {
                    periodData.onTimeCount++;
                }
                const orderDateForHistory = order.orderDate ? new Date(order.orderDate) : null;
                const orderDateForHistoryUtc = orderDateForHistory ? Date.UTC(orderDateForHistory.getFullYear(), orderDateForHistory.getMonth(), orderDateForHistory.getDate()) : null;

                if (orderDateForHistoryUtc) {
                    const diffTime = actualDeliveryDateForChartUtc - orderDateForHistoryUtc;
                    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                    periodData.totalDeliveryDays += diffDays;
                }
            }
        }

        // 2. For Pedidos Vencidos (conteo por periodo) - Now uses the 'wasEverOverdue' logic
        const expectedDateObj = new Date(order.expectedDeliveryDate);
        const expectedDatePeriodKey = generatePeriodKey(expectedDateObj, filter);
        const periodDataForOverdue = periodDataMap.get(expectedDatePeriodKey);

        if (periodDataForOverdue) {
            // Re-evaluate the alert status for this order based on its full history
            const alertStatusForChart = getDeliveryAlert(order); // Use the new getDeliveryAlert

            if (alertStatusForChart.text === 'Vencido' && !periodDataForOverdue.overdueHistoricalIds.has(order.id)) {
                periodDataForOverdue.overdueHistoricalIds.add(order.id);
            }
        }
    });

    // Finalize data for charts
    const finalOverdueData = Array.from(periodDataMap.values()).map(data => ({
        month: data.month, // This will be the period key (YYYY-MM, YYYY-Q1, YYYY-SW)
        value: data.overdueHistoricalIds.size
    }));

    const finalOnTimeData = Array.from(periodDataMap.values()).map(data => {
        const onTimeRate = data.totalCompletedCount > 0
            ? (data.onTimeCount / data.totalCompletedCount) * 100
            : 0;
        return { 
            month: data.month, 
            value: parseFloat(onTimeRate.toFixed(2)),
            meta: 95 // Add meta for compliance chart
        };
    });

    const finalAvgDeliveryTimeData = Array.from(periodDataMap.values()).map(data => {
        const avgDeliveryTime = data.totalCompletedCount > 0
            ? data.totalDeliveryDays / data.totalCompletedCount
            : 0;
        return { month: data.month, value: parseFloat(avgDeliveryTime.toFixed(1)) };
    });

    setMonthlyOverdueData(finalOverdueData);
    setMonthlyOnTimeData(finalOnTimeData);
    setMonthlyAvgDeliveryTimeData(finalAvgDeliveryTimeData);

    // Set overall indicators
    setOverallOverdueCount(currentOverdueCount);
    setOverallRescheduledCount(currentRescheduledCount);
    setOverallOnTimeRate(currentTotalCompletedDeliveries > 0 ? (currentOnTimeDeliveries / currentTotalCompletedDeliveries) * 100 : 0);
    setOverallAvgDeliveryTime(currentTotalCompletedDeliveries > 0 ? currentTotalDeliveryDays / currentTotalCompletedDeliveries : 0);
  };


  useEffect(() => {
    if (!authLoading && (userProfile?.rol === 'Administrador' || userProfile?.rol === 'Gerencia')) {
      setLoading(true);
      setError('');

      const unsubscribe = onSnapshot(ordersCollectionRef, (snapshot) => {
        let count = 0;
        const statusCounts = {};
        const fetchedOrders = [];

        snapshot.forEach(doc => {
          const data = doc.data();
          let expectedDeliveryDateFormatted = '';
          if (data.expectedDeliveryDate) {
            if (typeof data.expectedDeliveryDate.toDate === 'function') {
              expectedDeliveryDateFormatted = data.expectedDeliveryDate.toDate().toISOString().split('T')[0];
            } else if (typeof data.expectedDeliveryDate === 'string') {
              expectedDeliveryDateFormatted = data.expectedDeliveryDate;
            }
          }

          let createdAtFormatted = 'N/A';
          if (data.createdAt) {
            if (typeof data.createdAt.toDate === 'function') {
              createdAtFormatted = data.createdAt.toDate().toISOString();
            } else if (typeof data.createdAt === 'string') {
              createdAtFormatted = data.createdAt;
            }
          }

          let orderDateFormatted = 'N/A';
          if (data.orderDate) {
            orderDateFormatted = data.orderDate;
          } else if (data.createdAt) {
             if (typeof data.createdAt.toDate === 'function') {
                orderDateFormatted = data.createdAt.toDate().toISOString().split('T')[0];
             } else if (typeof data.createdAt === 'string') {
                orderDateFormatted = data.createdAt.split('T')[0];
             }
          }

          const orderData = { 
            id: doc.id, 
            ...data,
            expectedDeliveryDate: expectedDeliveryDateFormatted,
            orderDate: orderDateFormatted,
            createdAt: createdAtFormatted
          };
          
          // Ensure statusHistory is an array and timestamps are Date objects
          orderData.statusHistory = (orderData.statusHistory || []).map(entry => ({
            ...entry,
            timestamp: entry.timestamp?.toDate ? entry.timestamp.toDate() : new Date(entry.timestamp)
          }));
          fetchedOrders.push(orderData);
          count++;
          statusCounts[orderData.currentStatus] = (statusCounts[orderData.currentStatus] || 0) + 1;
        });

        setTotalOrders(count);
        setOrdersByStatus(statusCounts);

        // --- NUEVA LÓGICA DE ORDENAMIENTO PARA LA TABLA allOrders ---
        const finalStatuses = ['Entregado Exitosamente', 'Pedido listo para Recojo', 'Entregado en Agencia'];
        
        const activeOrders = fetchedOrders.filter(order => !finalStatuses.includes(order.currentStatus));
        const completedOrders = fetchedOrders.filter(order => finalStatuses.includes(order.currentStatus));

        // 1. Ordenar pedidos activos por expectedDeliveryDate (ascendente)
        activeOrders.sort((a, b) => {
            const dateA = new Date(a.expectedDeliveryDate);
            const dateB = new Date(b.expectedDeliveryDate);
            // Manejar fechas inválidas o nulas para que vayan al final de los activos
            if (!a.expectedDeliveryDate || a.expectedDeliveryDate === 'N/A') return 1;
            if (!b.expectedDeliveryDate || b.expectedDeliveryDate === 'N/A') return -1;
            return dateA.getTime() - dateB.getTime();
        });

        // 2. Ordenar pedidos completados por createdAt (descendente)
        completedOrders.sort((a, b) => {
            const dateA = new Date(a.createdAt);
            const dateB = new Date(b.createdAt);
            return dateB.getTime() - dateA.getTime();
        });

        // Combinar: activos primero, luego completados
        setAllOrders([...activeOrders, ...completedOrders]);
        // --- FIN NUEVA LÓGICA DE ORDENAMIENTO ---


        // Pass the timeFilter to aggregateDataAndCalculateOverall
        aggregateDataAndCalculateOverall(fetchedOrders, timeFilter);

        setLoading(false);
      }, (err) => {
        console.error("Error fetching real-time statistics:", err);
        setError("Could not load statistics. Please try again later.");
        setLoading(false);
      });

      return () => unsubscribe();
    } else if (!authLoading && userProfile?.rol !== 'Administrador' && userProfile?.rol !== 'Gerencia') {
      setLoading(false);
      setError('No tienes permisos para ver esta página.');
    }
  }, [authLoading, userProfile, timeFilter]);


  const pieChartData = Object.keys(ordersByStatus).map(status => ({
    name: status,
    value: ordersByStatus[status]
  }));

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ff7300'];

  const handleViewHistoryClick = (order) => {
    setSelectedOrder(order);
    setShowHistoryModal(true);
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '50px' }} className="app-content-container">Cargando estadísticas...</div>;
  }

  if (error) {
    return <div className="login-error app-content-container" style={{ textAlign: 'center', padding: '50px' }}>{error}</div>;
  }

  return (
    <div className="app-content-container">
      <h2 style={{ textAlign: 'center', color: 'var(--secondary-blue)' }}>Estadísticas de Pedidos</h2>

      {/* Summary Cards */}
      <div className="statistics-cards-grid">
        <div className="statistics-card">
          <h3>Total Pedidos</h3>
          <p className="card-value total">{totalOrders}</p>
        </div>
        <div className="statistics-card">
          <h3>Cumplimiento a Tiempo</h3>
          <p className="card-value success">{overallOnTimeRate.toFixed(1)}%</p>
        </div>
        <div className="statistics-card">
          <h3>Pedidos Vencidos</h3>
          <p className="card-value danger">{overallOverdueCount}</p>
        </div>
        <div className="statistics-card">
          <h3>Tiempo Promedio Entrega</h3>
          <p className="card-value info">{overallAvgDeliveryTime.toFixed(1)} días</p>
        </div>
        <div className="statistics-card">
          <h3>Pedidos Reprogramados</h3>
          <p className="card-value warning">{overallRescheduledCount}</p>
        </div>
        <div className="statistics-card">
          <h3>Entregado Exitosamente</h3>
          <p className="card-value success">{ordersByStatus['Entregado Exitosamente'] || 0}</p>
        </div>
        <div className="statistics-card">
          <h3>Entregado en Agencia</h3>
          <p className="card-value info">{ordersByStatus['Entregado en Agencia'] || 0}</p>
        </div>
        <div className="statistics-card">
          <h3>Programado para despacho</h3>
          <p className="card-value warning">{ordersByStatus['Programado para despacho'] || 0}</p>
        </div>
        <div className="statistics-card">
          <h3>En Ruta</h3>
          <p className="card-value primary">{ordersByStatus['En Ruta'] || 0}</p>
        </div>
        <div className="statistics-card">
          <h3>Reprogramado</h3>
          <p className="card-value danger">{ordersByStatus['Reprogramado'] || 0}</p>
        </div>
      </div>

      {/* Status Distribution Chart */}
      <h3 style={{ textAlign: 'center', marginTop: '40px', color: 'var(--secondary-blue)' }}>Distribución de Pedidos por Estado Actual</h3>
      {pieChartData.length > 0 ? (
        <ResponsiveContainer width="100%" height={400}>
          <PieChart>
            <Pie
              data={pieChartData}
              cx="50%"
              cy="50%"
              outerRadius={150}
              fill="#8884d8"
              dataKey="value"
              label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
            >
              {pieChartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      ) : (
        <p style={{ textAlign: 'center', color: '#666' }}>No hay datos para mostrar el gráfico.</p>
      )}

      {/* Order Details and Status Timeline Table */}
      <h3 style={{ textAlign: 'center', marginTop: '40px', color: 'var(--secondary-blue)' }}>Detalle de Pedidos y Cronología de Estados</h3>
      {allOrders.length === 0 ? (
        <p style={{ textAlign: 'center', color: '#666' }}>No hay pedidos para mostrar.</p>
      ) : (
        <div className="table-responsive">
          <table className="data-table">
            <thead>
              <tr>
                <th>Número Pedido</th>
                <th>Cliente</th>
                <th>Fecha Creación</th>
                <th>Fecha Prevista</th>
                <th>Fecha Entrega Exitosa</th>
                <th>Días de Retraso</th>
                <th>Estado Actual</th>
                <th>Alerta de Entrega</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {allOrders.map(order => {
                const alert = getDeliveryAlert(order);

                // Get Successful Delivery Date
                const deliveredStatusEntry = order.statusHistory?.find(h => h.status === 'Entregado Exitosamente');
                const actualDeliveryDate = deliveredStatusEntry?.timestamp ? (deliveredStatusEntry.timestamp.toDate ? deliveredStatusEntry.timestamp.toDate() : new Date(deliveredStatusEntry.timestamp)) : null;
                const actualDeliveryDateFormatted = actualDeliveryDate ? actualDeliveryDate.toISOString().split('T')[0] : 'N/A';

                // Calculate Days of Delay
                let daysOfDelay = 'N/A';
                if (actualDeliveryDate && order.expectedDeliveryDate) {
                  // Parse expected date string into UTC components
                  const partsExpected = order.expectedDeliveryDate.split('-').map(Number);
                  const expectedDateUtcMillis = Date.UTC(partsExpected[0], partsExpected[1] - 1, partsExpected[2]);
                  
                  // Get actual delivery date into UTC components
                  const actualDateUtcMillis = Date.UTC(actualDeliveryDate.getFullYear(), actualDeliveryDate.getMonth(), actualDeliveryDate.getDate());

                  const diffTime = actualDateUtcMillis - expectedDateUtcMillis;
                  // Use Math.floor to get full days of delay. If diffTime is negative or zero, diffDays will be <= 0.
                  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)); 
                  
                  // Show "0" if no delay (diffDays <= 0), or the number of days if positive delay.
                  daysOfDelay = diffDays > 0 ? `${diffDays} día(s)` : '0';
                }

                return (
                  <tr key={order.id}>
                    <td data-label="Número Pedido">{order.orderNumber}</td>
                    <td data-label="Cliente">{order.clientName}</td>
                    <td data-label="Fecha Creación">{order.orderDate}</td>
                    <td data-label="Fecha Prevista">{order.expectedDeliveryDate}</td>
                    <td data-label="Fecha Entrega Exitosa">{actualDeliveryDateFormatted}</td>
                    <td data-label="Días de Retraso">{daysOfDelay}</td>
                    <td data-label="Estado Actual" style={{ color: alert.color, fontWeight: 'bold' }}>
                      {order.currentStatus}
                    </td>
                    <td data-label="Alerta de Entrega" style={{ color: alert.color, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '5px' }}>
                      {alert.icon} {alert.text}
                    </td>
                    <td data-label="Acciones" className="action-buttons">
                      <button
                        onClick={() => handleViewHistoryClick(order)}
                        style={{
                          backgroundColor: '#17A2B8',
                          color: 'white',
                          fontWeight: 'bold',
                          borderRadius: '8px',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '8px 12px',
                          transition: 'background-color 0.3s ease, transform 0.2s ease',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                      >
                        Ver Historial
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Delivery Performance Trends Section */}
      <h3 style={{ textAlign: 'center', marginTop: '40px', color: 'var(--secondary-blue)' }}>Tendencias de Rendimiento de Entrega ({timeFilter === 'weekly' ? 'Semanal' : timeFilter === 'fortnightly' ? 'Quincenal' : 'Mensual'})</h3>
      
      {/* Time filter for charts */}
      <div ref={filterOptionsRef} className="filter-options" style={{ textAlign: 'center', marginBottom: '20px' }}>
        <label style={{ marginRight: '15px', fontWeight: 'bold' }}>Ver por:</label>
        <input
          type="radio"
          id="monthly"
          name="timeFilter"
          value="monthly"
          checked={timeFilter === 'monthly'}
          onChange={(e) => setTimeFilter(e.target.value)}
          style={{ marginRight: '5px' }}
        />
        <label htmlFor="monthly" style={{ marginRight: '15px' }}>Mensual</label>

        <input
          type="radio"
          id="fortnightly"
          name="timeFilter"
          value="fortnightly"
          checked={timeFilter === 'fortnightly'}
          onChange={(e) => setTimeFilter(e.target.value)}
          style={{ marginRight: '5px' }}
        />
        <label htmlFor="fortnightly" style={{ marginRight: '15px' }}>Quincenal</label>

        <input
          type="radio"
          id="weekly"
          name="timeFilter"
          value="weekly"
          checked={timeFilter === 'weekly'}
          onChange={(e) => setTimeFilter(e.target.value)}
          style={{ marginRight: '5px' }}
        />
        <label htmlFor="weekly">Semanal</label>
      </div>


      {/* Overdue Orders Chart */}
      <div className="chart-card">
        <h4 style={{ textAlign: 'center', color: 'var(--primary-red)' }}>Pedidos Vencidos por Periodo</h4>
        {monthlyOverdueData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={monthlyOverdueData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="value" stroke="#DC3545" name="Pedidos Vencidos" />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <p style={{ textAlign: 'center', color: '#666' }}>No hay datos históricos de pedidos vencidos.</p>
        )}
      </div>

      {/* On-Time Compliance Rate Chart */}
      <div className="chart-card">
        <h4 style={{ textAlign: 'center', color: '#20C997' }}>Tasa de Cumplimiento a Tiempo (%) por Periodo</h4>
        {monthlyOnTimeData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={monthlyOnTimeData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis domain={[0, 100]} label={{ value: '%', angle: -90, position: 'insideLeft' }} />
              <Tooltip formatter={(value) => `${value.toFixed(2)}%`} />
              <Legend />
              <Line type="monotone" dataKey="value" stroke="#20C997" name="Cumplimiento a Tiempo" />
              {/* 95% Target Line */}
              <Line type="monotone" dataKey="meta" stroke="#FF0000" name="Meta (95%)" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <p style={{ textAlign: 'center', color: '#666' }}>No hay datos históricos de cumplimiento a tiempo.</p>
        )}
      </div>

      {/* Average Delivery Time Chart */}
      <div className="chart-card">
        <h4 style={{ textAlign: 'center', color: 'var(--secondary-blue)' }}>Tiempo Promedio de Entrega (Días) por Periodo</h4>
        {monthlyAvgDeliveryTimeData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={monthlyAvgDeliveryTimeData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis label={{ value: 'Días', angle: -90, position: 'insideLeft' }} />
              <Tooltip formatter={(value) => `${value.toFixed(1)} días`} />
              <Legend />
              <Line type="monotone" dataKey="value" stroke="var(--secondary-blue)" name="Días Promedio" />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <p style={{ textAlign: 'center', color: '#666' }}>No hay datos históricos de tiempo promedio de entrega.</p>
        )}
      </div>


      {/* Order History Modal */}
      {showHistoryModal && selectedOrder && (
        <OrderHistoryModal
          order={selectedOrder}
          onClose={() => setShowHistoryModal(false)}
        />
      )}
    </div>
  );
}

export default Statistics;